import React from "react";
import {
  Grid,
  TextField,
  MenuItem,
  Typography,
  Box,
  Stack,
  InputAdornment,
  IconButton,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import OilBarrelOutlinedIcon from "@mui/icons-material/OilBarrelOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import CallIcon from "@mui/icons-material/Call";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import toast from "react-hot-toast";

// Strip everything except digits and the leading `+` so we can build a
// clean `tel:` URI from values like "9486172528, 6381456241".
const telHref = (phone) => {
  if (!phone) return null;
  const digits = String(phone).replace(/[^\d+]/g, "");
  return digits ? `tel:${digits}` : null;
};

const copyToClipboard = async (text, label = "Phone") => {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch {
    toast.error("Copy failed");
  }
};

// Trailing call + copy action buttons inside a phone-field input.
// Greyed out and disabled when the field is empty.
const PhoneFieldAdornment = ({ value, label }) => {
  const hasValue = Boolean((value || "").trim());
  const href = telHref(value);
  return (
    <InputAdornment position="end">
      <Tooltip title={hasValue ? `Call ${value}` : "No number"}>
        <span>
          <IconButton
            size="small"
            component={hasValue ? "a" : "button"}
            href={hasValue ? href : undefined}
            disabled={!hasValue}
            sx={{ p: 0.5 }}
          >
            <CallIcon sx={{ fontSize: 16, color: hasValue ? "success.main" : "action.disabled" }} />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={hasValue ? `Copy ${label}` : "No number"}>
        <span>
          <IconButton
            size="small"
            onClick={() => copyToClipboard(value, label)}
            disabled={!hasValue}
            sx={{ p: 0.5 }}
          >
            <ContentCopyOutlinedIcon
              sx={{ fontSize: 14, color: hasValue ? "primary.main" : "action.disabled" }}
            />
          </IconButton>
        </span>
      </Tooltip>
    </InputAdornment>
  );
};

const Section = ({ icon, title, children, last = false }) => (
  <Box sx={{ mb: last ? 0 : 3 }}>
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        mb: 1.5,
        pb: 1,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "8px",
          backgroundColor: "primary.light",
          color: "primary.main",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Typography
        sx={{
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          color: "text.primary",
          fontSize: "0.75rem",
        }}
      >
        {title}
      </Typography>
    </Stack>
    {children}
  </Box>
);

const fieldSx = { "& .MuiInputBase-input": { fontSize: "0.875rem" } };

const fallbackStatuses = ["live", "production", "testing", "development", "removed"];

export default function CompanyFormFields({ formik, statuses = [] }) {
  const f = (name, label, opts = {}) => (
    <TextField
      fullWidth
      size="small"
      label={label}
      name={name}
      value={formik.values[name] ?? ""}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
      error={formik.touched[name] && Boolean(formik.errors[name])}
      helperText={formik.touched[name] && formik.errors[name]}
      sx={fieldSx}
      InputLabelProps={opts.type === "date" ? { shrink: true } : undefined}
      {...opts}
    />
  );

  // Phone field with trailing call + copy buttons. Disabled when empty.
  const pf = (name, label) =>
    f(name, label, {
      InputProps: {
        endAdornment: <PhoneFieldAdornment value={formik.values[name]} label={label} />,
      },
    });

  return (
    <>
      <Section icon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />} title="Basic">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>{f("name", "Name *")}</Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth size="small" select label="Status" name="status"
              value={formik.values.status} onChange={formik.handleChange}
              sx={fieldSx}
            >
              {(statuses.length ? statuses : fallbackStatuses).map((s) => (
                <MenuItem key={s} value={s} sx={{ fontSize: "0.8125rem" }}>{s}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Section>

      <Section icon={<PlaceOutlinedIcon sx={{ fontSize: 16 }} />} title="Location">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>{f("district", "District")}</Grid>
          <Grid item xs={12} sm={6} md={4}>{f("block", "Block")}</Grid>
          <Grid item xs={12} sm={6} md={4}>{f("panchayat", "Panchayat")}</Grid>
          <Grid item xs={12} sm={6} md={4}>{f("installation_date", "Installation Date", { type: "date" })}</Grid>
          <Grid item xs={12} sm={6} md={4}>{f("expiry_date", "Expiry Date", { type: "date" })}</Grid>
        </Grid>
      </Section>

      <Section icon={<PersonOutlinedIcon sx={{ fontSize: 16 }} />} title="Operator & Secretary">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>{f("operator_name", "Operator Name")}</Grid>
          <Grid item xs={12} sm={6} md={4}>{pf("operator_phone", "Operator Phone")}</Grid>
          <Grid item xs={12} sm={6} md={4}>{pf("local_electrician_number", "Electrician Phone")}</Grid>
          <Grid item xs={12} sm={6} md={4}>{f("secretary_name", "Secretary Name")}</Grid>
          <Grid item xs={12} sm={6} md={4}>{pf("secretary_phone", "Secretary Phone")}</Grid>
        </Grid>
      </Section>

      <Section icon={<BoltOutlinedIcon sx={{ fontSize: 16 }} />} title="Motor / Inverter">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>{f("inverter_model", "Inverter Model")}</Grid>
          <Grid item xs={12} sm={6} md={4}>{f("motor_imei", "Motor IMEI")}</Grid>
          <Grid item xs={12} sm={6} md={4}>{f("motor_sim_number", "Motor SIM")}</Grid>
          <Grid item xs={12} sm={6} md={4}>{f("motor_make_hp", "Motor Make / HP")}</Grid>
          <Grid item xs={12} sm={6} md={4}>{f("eb_amps_rate_running", "EB Amps (running)", { type: "number" })}</Grid>
          <Grid item xs={12} sm={6} md={4}>{f("motor_location_tag", "Motor Location Tag")}</Grid>
          <Grid item xs={12} sm={6} md={4}>{f("eb_meter_number", "EB Meter Number")}</Grid>
        </Grid>
      </Section>

      <Section icon={<OilBarrelOutlinedIcon sx={{ fontSize: 16 }} />} title="OHT / Tank">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>{f("oht_imei", "OHT IMEI")}</Grid>
          <Grid item xs={12} sm={6} md={4}>{f("oht_sim_number", "OHT SIM")}</Grid>
          <Grid item xs={12} sm={6} md={4}>{f("oht_depth_meter", "OHT Depth (m)", { type: "number" })}</Grid>
          <Grid item xs={12} sm={6} md={4}>{f("tank_capacity", "Tank Capacity (L)", { type: "number" })}</Grid>
          <Grid item xs={12} sm={6} md={4}>{f("oht_location_tag", "OHT Location Tag")}</Grid>
        </Grid>
      </Section>

      <Section icon={<NotesOutlinedIcon sx={{ fontSize: 16 }} />} title="Other" last>
        <Grid container spacing={2}>
          <Grid item xs={12}>{f("remark", "Remark", { multiline: true, minRows: 3 })}</Grid>
        </Grid>
      </Section>
    </>
  );
}

const todayISO = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};

const oneYearFromTodayISO = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
};

export const buildEmptyValues = () => ({
  name: "",
  status: "production",
  inverter_model: "",
  motor_imei: "",
  motor_sim_number: "",
  motor_make_hp: "",
  eb_amps_rate_running: "",
  motor_location_tag: "",
  operator_name: "",
  operator_phone: "",
  secretary_name: "",
  secretary_phone: "",
  oht_imei: "",
  oht_sim_number: "",
  oht_depth_meter: "",
  tank_capacity: "",
  oht_location_tag: "",
  district: "",
  block: "",
  panchayat: "",
  installation_date: todayISO(),
  expiry_date: oneYearFromTodayISO(),
  remark: "",
  eb_meter_number: "",
  local_electrician_number: "",
});

// kept for edit-mode spreads where date values are overridden by the company doc
export const emptyValues = {
  ...buildEmptyValues(),
  installation_date: "",
  expiry_date: "",
};

export const numericFields = [
  "manufacturing_id",
  "eb_amps_rate_running",
  "oht_depth_meter",
  "tank_capacity",
];

export const dateFields = ["installation_date", "expiry_date"];

export const toDateInput = (d) =>
  d ? new Date(d).toISOString().slice(0, 10) : "";

export const normalizePayload = (values) => {
  const payload = { ...values };
  numericFields.forEach((k) => {
    if (payload[k] === "" || payload[k] == null) delete payload[k];
    else payload[k] = Number(payload[k]);
  });
  dateFields.forEach((k) => {
    if (!payload[k]) delete payload[k];
  });
  return payload;
};
